"use client";

import { useEffect, useRef, useState } from "react";
import { COPILOT_CONVERSATION_PROXY_ROUTE } from "@/utils/constants";
import { resolveStoredConversationId } from "@/utils/copilotConversationId";
import { replaceUrlParams } from "@/utils/replaceUrlParams";

function getConversationIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const id = new URLSearchParams(window.location.search).get("conversationId");
  if (!id || id === "undefined" || id === "null" || !id.trim()) return null;
  return id;
}

interface UseResolveCopilotConversationIdOptions {
  /** True once the user is confirmed logged in. */
  isLoggedIn: boolean;
  /**
   * Pass true while auth state is still being loaded.
   * The hook will hold off fetching from the backend until this is false.
   */
  skip: boolean;
  /** Scopes the stored conversation to a specific copilot surface. Defaults to "support". */
  copilotId?: string;
  /**
   * Skip any URL or stored conversation and start a new one. Clears
   * `?conversationId=` from the URL so CopilotProvider treats it as new.
   */
  forceNew?: boolean;
}

interface UseResolveCopilotConversationIdResult {
  /**
   * An existing conversation to resume, or undefined to let CopilotProvider
   * generate a new one. Only meaningful once `isLoading` is false.
   */
  conversationId: string | undefined;
  isLoading: boolean;
}

/**
 * Resolves the copilot conversationId to seed CopilotProvider's
 * `initialConversationId` before the conversation is loaded.
 *
 * Priority:
 * 0. `forceNew` — resolves to undefined immediately (no lookup).
 * 1. `?conversationId=` URL param — resolved synchronously after mount.
 * 2. Stored ID from the backend (GET /copilot-conversation) — for logged-in
 *    users when no URL param is present. The URL is updated via replaceState
 *    so that CopilotProvider can load the conversation history.
 * 3. undefined — no stored ID, logged out, or lookup failed.
 *
 * For new conversations nothing is written to the URL: CopilotProvider
 * generates the ID and writes it itself. If `?conversationId=` were present at
 * provider mount, it would treat the empty conversation as "returning" and
 * retry the history load (~4.5s of loading state).
 *
 * `isLoading` is true until one of the above resolves.
 * Pass `skip: true` while auth is still in-flight to defer the backend fetch.
 */
export function useResolveCopilotConversationId({
  isLoggedIn,
  skip,
  copilotId,
  forceNew = false,
}: UseResolveCopilotConversationIdOptions): UseResolveCopilotConversationIdResult {
  const [state, setState] = useState<UseResolveCopilotConversationIdResult>({
    conversationId: undefined,
    isLoading: true,
  });
  // Once forced new, never strip the URL again: by then CopilotProvider may
  // have written the new conversation's ID, which must survive a refresh.
  const hasForcedNew = useRef(false);

  useEffect(() => {
    // Each effect invocation gets its own `cancelled` flag. The cleanup
    // function sets it to true when the effect is torn down (including
    // React StrictMode's extra mount/unmount cycle in development), which
    // prevents a stale async response from updating state after the effect
    // has been superseded.
    let cancelled = false;
    const cleanup = () => {
      cancelled = true;
    };

    if (forceNew) {
      if (hasForcedNew.current) return cleanup;
      hasForcedNew.current = true;
      replaceUrlParams((params) => params.delete("conversationId"));
      setState({ conversationId: undefined, isLoading: false });
      return cleanup;
    }

    // Always check the URL first. This handles both the common "navigate with
    // ?conversationId=" case and the SSR→client transition where the initial
    // state was empty (no window during server render).
    //
    // IMPORTANT: this must only run after CopilotProvider has been prevented
    // from mounting (by gating on isLoading in the parent). CopilotProvider
    // writes its conversationId to the URL on mount, which would pollute this
    // read and skip the stored-ID lookup.
    const urlId = getConversationIdFromUrl();
    if (urlId) {
      setState({ conversationId: urlId, isLoading: false });
      return cleanup;
    }

    // Hold off until auth state has settled (e.g. while useCurrentUserContext
    // is still fetching).
    if (skip) return cleanup;

    const resolve = (id: string | null) => {
      if (cancelled) return;
      if (id) replaceUrlParams((params) => params.set("conversationId", id));
      setState({ conversationId: id ?? undefined, isLoading: false });
    };

    if (!isLoggedIn) {
      // No session — can't look up a stored conversation id.
      resolve(null);
      return cleanup;
    }

    // Fetch the stored conversationId for this user from the backend.
    const url = copilotId
      ? `${COPILOT_CONVERSATION_PROXY_ROUTE}?copilot_id=${encodeURIComponent(copilotId)}`
      : COPILOT_CONVERSATION_PROXY_ROUTE;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        // Treat conversations idle for more than a day as expired —
        // resuming them would drop the user into stale context.
        resolve(resolveStoredConversationId(data));
      })
      .catch(() => {
        // Network/auth error — start a new conversation rather than blocking.
        resolve(null);
      });

    return cleanup;
  }, [skip, isLoggedIn, copilotId, forceNew]);

  return state;
}
